import clsx from 'clsx';
import React, {useCallback} from 'react';
import {useMatch, useParams} from 'react-router-dom';
import {Button, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {getEnv} from '@/config/environment';
import {ROUTES} from '@/config/routesConstants';
import BackButton from '@/components/brand-header/child-components/back-button/BackButton';
import {useBreadcrumbsBack} from '@/components/brand-header/child-components/breadcrumbs-header';
import useOffPlatformBreadcrumbItems from '@/components/brand-header/child-components/breadcrumbs-header/hooks/useOffPlatformBreadcrumbItems';
import Breadcrumbs from '@/components/breadcrumbs';
import {MenuPopularPaysLogo} from '@/components/unusual-icons';
import useGoBack from '@/hooks/use-go-back';
import styles from './OffPlatformHeader.module.scss';

// RDS Button with `href` renders an <a>, but its props type is still a <button>,
// so `target`/`rel` cannot be passed as JSX attributes. Spreading them is typed.
const NEW_TAB_ANCHOR_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

function OffPlatformHeader() {
  const {campaignId} = useParams<{campaignId: string}>();
  const isBaseRoute = useMatch(`${ROUTES.OFF_PLATFORM_REVIEW}/:campaignId`);
  const breadcrumbItems = useOffPlatformBreadcrumbItems();
  const breadcrumbsBackDestination = useBreadcrumbsBack();
  const tryGoBack = useGoBack();

  const hasBreadcrumbs = (breadcrumbItems?.length ?? 0) > 0;
  const showBreadcrumbs = !isBaseRoute && hasBreadcrumbs;

  const handleBack = useCallback(() => {
    tryGoBack(breadcrumbsBackDestination ?? undefined);
  }, [tryGoBack, breadcrumbsBackDestination]);

  return (
    <div
      className={clsx(styles.header, {
        // On detail routes (where breadcrumbs render), the ContentSubmissionSubHeader
        // below already draws the separating line between header chrome and page
        // content. Drop our own border to avoid a visible double line.
        [styles.headerNoBorder]: showBreadcrumbs,
      })}
    >
      {showBreadcrumbs ? (
        <>
          <BackButton onBack={handleBack} />
          <Breadcrumbs items={breadcrumbItems ?? []} />
        </>
      ) : (
        <MenuPopularPaysLogo height={32} />
      )}
      {campaignId ? (
        <div className={styles.briefLink}>
          <Button
            appearance="neutral"
            mode="tinted"
            size="small"
            testID="campaign-brief-link"
            href={`${getEnv().VITE_CWA_URL}/preview/campaign/${campaignId}`}
            icon={
              <Icon size="small" appearance="neutral" name="Actions-Forward" />
            }
            iconPosition="end"
            {...NEW_TAB_ANCHOR_PROPS}
          >
            {translate('components.campaign-brief-link.open-brief-link')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default OffPlatformHeader;
